/**
 * EmailTemplateEditor — pannello "Modifica testi email" dentro la Email
 * Preview Console. Permette a TUTTI gli admin (req. Mara 09/07/2026) di
 * personalizzare i testi delle 3 email del funnel cliente:
 * welcome · otp_signature · contract_signed.
 *
 * I testi custom vengono salvati in `email_templates` (Mongo) e usati
 * sia dagli invii reali sia dall'anteprima. "Ripristina" torna ai
 * default di piattaforma.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileEdit, Loader2, RotateCcw, Save, BadgeCheck, Languages, HelpCircle } from "lucide-react";
import { API } from "../../config";
import { EmailTemplateTranslateModal } from "./EmailTemplateTranslateModal";
import { EmailTemplateGuideModal } from "./EmailTemplateGuideModal";

const EDITABLE_TYPES = ["welcome", "otp_signature", "contract_signed"];

export const EmailTemplateEditor = ({ emailType, country, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [showTranslate, setShowTranslate] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const editable = EDITABLE_TYPES.includes(emailType);

  const load = useCallback(async () => {
    if (!editable) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/email-templates/${emailType}?country=${country}`);
      setData(r.data);
      const initial = {};
      (r.data.fields_meta || []).forEach(({ key }) => {
        initial[key] = r.data.override_fields?.[key] ?? "";
      });
      setValues(initial);
    } catch {
      toast.error("Impossibile caricare i testi email");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [emailType, country, editable]);

  useEffect(() => { load(); }, [load]);

  if (!editable) return null;

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/email-templates/${emailType}`, {
        country,
        fields: values,
      });
      toast.success(r.data.has_override
        ? "Testi personalizzati salvati"
        : "Nessun testo personalizzato: ripristinati i default");
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Salvataggio fallito");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/email-templates/${emailType}?country=${country}`);
      toast.success("Testi ripristinati ai default di piattaforma");
      await load();
      onSaved?.();
    } catch {
      toast.error("Ripristino fallito");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#111] border border-gray-800 rounded-2xl p-5" data-testid="email-template-editor-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-2">
          <FileEdit size={12} />
          Modifica testi email
          {data?.has_override && (
            <span className="text-emerald-400 flex items-center gap-1 normal-case tracking-normal" data-testid="template-override-badge">
              <BadgeCheck size={12} /> personalizzata
            </span>
          )}
        </h3>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowGuide(true)}
            className="text-xs text-gray-500 hover:text-white transition flex items-center gap-1"
            title="Apri la guida all'editor testi email"
            data-testid="email-template-guide-btn"
          >
            <HelpCircle size={12} />
            Guida
          </button>
          <button
            onClick={() => setOpen(o => !o)}
            className="text-xs text-lojack-blue-400 hover:text-lojack-blue-300 transition"
            data-testid="email-template-editor-toggle"
          >
            {open ? "Nascondi" : "Modifica"}
          </button>
        </div>
      </div>

      {!open && (
        <p className="text-gray-500 text-[11px] leading-relaxed">
          {data?.has_override
            ? `Testi personalizzati attivi per ${data.country}${data.updated_by ? ` (ultima modifica: ${data.updated_by})` : ""}.`
            : "Questa email usa i testi di default della piattaforma. Clicca Modifica per personalizzarli."}
        </p>
      )}

      {open && loading && (
        <div className="flex justify-center py-4"><Loader2 size={18} className="animate-spin text-gray-500" /></div>
      )}

      {open && !loading && data && (
        <div className="space-y-3 mt-2" data-testid="email-template-editor-fields">
          {data.country !== data.requested_country && (
            <p className="text-lojack-clause-200/80 text-[10px] leading-relaxed bg-lojack-clause-500/10 border border-lojack-clause-500/30 rounded-lg px-2.5 py-2">
              {data.requested_country} usa il pacchetto lingua <strong>{data.country}</strong>: le modifiche valgono per entrambi.
            </p>
          )}
          {(data.fields_meta || []).map(({ key, label, multiline, placeholders }) => {
            const customized = (values[key] ?? "").trim().length > 0;
            return (
              <div key={key}>
                <div className="flex items-center justify-between">
                  <label className="text-gray-500 text-[10px] uppercase tracking-wide">
                    {label}
                    {customized && <span className="text-emerald-400 ml-1.5 normal-case">●</span>}
                  </label>
                  {placeholders?.length > 0 && (
                    <span className="text-[9px] text-gray-600 font-mono">{placeholders.join(" ")}</span>
                  )}
                </div>
                {multiline ? (
                  <textarea
                    value={values[key] ?? ""}
                    onChange={e => setValues(v => ({ ...v, [key]: e.target.value }))}
                    placeholder={data.defaults[key]}
                    rows={3}
                    className="w-full mt-1 bg-[#0a0a0a] border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white focus:border-lojack-blue-500 outline-none resize-y"
                    data-testid={`template-field-${key}`}
                  />
                ) : (
                  <input
                    value={values[key] ?? ""}
                    onChange={e => setValues(v => ({ ...v, [key]: e.target.value }))}
                    placeholder={data.defaults[key]}
                    className="w-full mt-1 bg-[#0a0a0a] border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white focus:border-lojack-blue-500 outline-none"
                    data-testid={`template-field-${key}`}
                  />
                )}
              </div>
            );
          })}

          <div className="flex items-center gap-2 pt-1.5">
            <button
              onClick={save}
              disabled={saving}
              className="flex-1 bg-lojack-blue-600 hover:bg-lojack-blue-700 disabled:opacity-50 rounded-lg px-3 py-2 text-xs font-semibold flex items-center justify-center gap-1.5 transition"
              data-testid="email-template-save-btn"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Salva testi
            </button>
            <button
              onClick={reset}
              disabled={saving || !data.has_override}
              className="flex-1 text-xs px-3 py-2 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800 disabled:opacity-40 transition flex items-center justify-center gap-1.5"
              data-testid="email-template-reset-btn"
            >
              <RotateCcw size={12} />
              Ripristina default
            </button>
          </div>
          {data.has_override && (
            <button
              onClick={() => setShowTranslate(true)}
              disabled={saving}
              className="w-full text-xs px-3 py-2 rounded-lg border border-lojack-blue-500/40 bg-lojack-blue-500/10 text-lojack-blue-300 hover:bg-lojack-blue-500/20 disabled:opacity-40 transition flex items-center justify-center gap-1.5"
              data-testid="email-template-translate-btn"
            >
              <Languages size={12} />
              Proponi traduzioni (AI) nelle altre lingue
            </button>
          )}
          <p className="text-gray-500 text-[10px] leading-relaxed italic">
            I campi vuoti mantengono il testo di default (mostrato in grigio come suggerimento).
            Le modifiche valgono per gli invii reali E per l&apos;anteprima. Ogni modifica è tracciata nell&apos;audit log.
          </p>
        </div>
      )}

      {showGuide && <EmailTemplateGuideModal onClose={() => setShowGuide(false)} />}

      {showTranslate && data && (
        <EmailTemplateTranslateModal
          emailType={emailType}
          sourceCountry={data.country}
          availableCountries={data.available_countries}
          fieldsMeta={data.fields_meta}
          onClose={() => setShowTranslate(false)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
};

export default EmailTemplateEditor;
